import { Button, Card, Checkbox, Flex, Input, Spin, Typography } from "antd";
import Alert from "antd/es/alert/Alert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAcceptBatchTrip, useCreateDeliveryProfile } from "../api/api";
import {
  useGetBatches,
  useGetDeliveryProfile,
  useGetDriverLocation,
  useGetSession,
} from "../api/api.get";
import { useNotifications2 } from "../components/notifications/notification";
import { useGeolocation } from "../hooks/useGeoLocation";
import { capitalize } from "../utils/functions";

export default function HomePage() {
  const {
    location: geoLocation,
    error: locationError,
    loading: isLocationLoading,
  } = useGeolocation();

  const navigate = useNavigate();
  const { data: profile, isLoading, error } = useGetDeliveryProfile();

  const [zipcode, setZipcode] = useState<string>();

  const { data: cityData, refetch: refetchDriverCity } = useGetDriverLocation(
    geoLocation?.lat,
    geoLocation?.lon,
    zipcode,
  );

  useEffect(() => {
    if (geoLocation || zipcode) {
      refetchDriverCity();
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [geoLocation, zipcode]);

  const { data: sessionData } = useGetSession(!!profile);

  const { data: batches, refetch } = useGetBatches(
    geoLocation?.lat,
    geoLocation?.lon,
  );

  const { mutateAsync } = useCreateDeliveryProfile();
  const { notification, notifyError, notifySuccess } = useNotifications2();

  const handleCreateProfile = () => {
    mutateAsync({})
      .then(() => {
        notifySuccess({
          title: "Success",
          description: "Profile successfully created",
        });
      })
      .catch(() => {
        notifyError();
      });
  };

  const { mutateAsync: acceptBatchTripAsync } = useAcceptBatchTrip();
  const handleAcceptTrip = (batchId: string) => {
    acceptBatchTripAsync({ batchId })
      .then(() => {
        navigate(`/batches/${batchId}`);
      })
      .catch(() => {
        notifyError("Whoops! Unable to accept. Please try another batch.");
      });
  };

  return (
    <div className="divColumn p-40" id="home-body">
      {notification}

      <div className="divSpread">
        {cityData && (
          <div className="divRight">
            <Alert
              type="success"
              message={
                <Flex className="divAlignItemsOnly">
                  <img
                    alt="location"
                    src="/assets/icons/location.svg"
                    style={{ width: 30 }}
                  />
                  <strong>{cityData?.city}</strong>
                </Flex>
              }
            />
          </div>
        )}

        {!cityData && <Alert type="error" message="Location unknown" />}

        <Flex gap={4}>
          Welcome<strong>{capitalize(sessionData?.firstname)}</strong>
        </Flex>
      </div>
      <div className="my-20">
        {isLocationLoading && (
          <Flex className="divCenterAlign w-100" vertical gap={20}>
            <span>Finding your location...</span>
            <Spin />
          </Flex>
        )}
        {isLoading && (
          <Flex className="divCenterAlign w-100" vertical gap={20}>
            <span>Loading profile...</span>
            <Spin />
          </Flex>
        )}

        {locationError && !batches && (
          <Flex vertical className="divCenterAlign w-100" gap={20}>
            <Flex vertical gap={10}>
              <Typography.Title level={5}>
                Please enter zipcode manually.
              </Typography.Title>
              <Flex gap={5}>
                <Input
                  value={zipcode}
                  style={{ width: 200 }}
                  onChange={(e) => setZipcode(e.target.value)}
                />
                <Button onClick={() => refetch()} type="primary">
                  Go
                </Button>
              </Flex>
            </Flex>

            <Flex vertical className="divCenterAlign w-100">
              <Typography.Title level={3}>
                Unable to find location
              </Typography.Title>

              <img
                src="/assets/icons/location-not-found.svg"
                style={{ width: "20%" }}
                alt="location-not-found"
              />
            </Flex>
          </Flex>
        )}

        {!isLoading && !isLocationLoading && !!cityData && (
          <>
            {error && (
              <>
                {(error as any).response?.data?.statusCode === 404 && (
                  <div>
                    <Flex className="divCenterAlign divColumn my-40" gap={20}>
                      <Flex className="divAlignItemsOnly" gap={10}>
                        <Checkbox /> I agree to the driver terms and conditions
                        for Delmaster and will upload the necessary documents
                        within 48 hours to confirm identity.
                      </Flex>

                      <Button onClick={() => handleCreateProfile()}>
                        Agree & Continue{" "}
                      </Button>
                    </Flex>
                  </div>
                )}
                {(error as any).response?.data?.statusCode !== 404 && (
                  <div>
                    <Flex className="divCenterAlign divColumn my-40" gap={20}>
                      <Alert
                        message="Profile not found. Please contact support 818 561 1139"
                        type="error"
                      />
                      <img
                        src="/assets/images/404.png"
                        alt="not-found"
                        style={{ width: 300, height: 300 }}
                      />
                    </Flex>
                  </div>
                )}
              </>
            )}
            <div className={`divColumn `}>
              {batches && batches.length > 0 ? (
                <Flex gap={16} wrap="wrap" vertical className="p-20">
                  {batches.map((batch, key) => (
                    <Card
                      className="my-20"
                      title={
                        <h2 className="divCenterAlign">
                          {capitalize(batch?.name)}
                        </h2>
                      }
                      variant="borderless"
                      key={key}
                    >
                      <Flex className="divCenterAlign" gap={10}>
                        <img
                          src="/assets/icons/shopping.svg"
                          alt="drive"
                          style={{ width: 35, height: 35 }}
                        />
                        <strong style={{ fontSize: 35 }}>
                          {batch.batch_deliveries?.length}
                        </strong>
                      </Flex>

                      <div className="my-10">
                        <Flex vertical className="divAlignItemsOnly" gap={0}>
                          <strong
                            style={{ fontSize: 20, color: "darkslategrey" }}
                          >
                            {Math.round(batch.distance_km * 0.621)} miles away
                          </strong>
                        </Flex>
                      </div>

                      <div className="mt-40">
                        <Button
                          onClick={() => handleAcceptTrip(batch.id)}
                          type="primary"
                          style={{ width: "100%" }}
                        >
                          Accept
                        </Button>
                      </div>
                    </Card>
                  ))}
                </Flex>
              ) : (
                <Alert
                  message="No deliveries available at this time."
                  type="info"
                  showIcon
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
